import { intersection } from 'lodash'

import { dataTransferWrap } from '../dataTransferService'

import dtConfig from '@/config/data-transfer'
import { ModulesMap } from '@/config/data/modulesMaps'

const elementMatchesQueryParams = (element, query) => {
	// console.log('elementMatchesQueryParams', element, query)
	const queryResult = {}
	Object.keys(query).forEach((key) => {
		queryResult[key] = null
		switch (key) {
			case 'legal_entity_ids':
				if (query[key].length > 0) {
					queryResult[key] = query[key].includes(element.entity)
				}
				break
			case 'machine_types':
				if (query[key].length > 0) {
					queryResult[key] = query[key].includes(element.machine_type)
				}
				break
			case 'machine_group_ids':
				if (query[key].length > 0) {
					queryResult[key] = intersection(query[key], element.groups).length >= 1
				}
				break
			case 'machine_ids':
				if (query[key].length > 0) {
					queryResult[key] = query[key].includes(element.id)
				}
				break
			case 'property_types':
				if (query[key].length > 0) {
					queryResult[key] = query[key].includes(element.property)
				}
				break
			default:
				queryResult[key] = query[key].includes(element[key])
				break
		}
	})
	return Object.values(queryResult).every((value) => value === true)
}

function createMachine(companyId, payload) {
	return dataTransferWrap(ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].createMachine, {
		urlParams: {
			companyId,
		},
		...payload
	})
}

function getAvailableMachines(companyId, query) {
	return new Promise((resolve, reject) => {
		dataTransferWrap(ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].getAvailableMachines, {
			urlParams: {
				companyId,
			}
		})
			.then((res) => {
				// console.log('getAvailableMachines', res, query)
				const possibleQueryParams = {}
				Object.keys(query ?? {}).forEach((key) => {
					if ([
						'legal_entity_ids',
						'machine_types',
						'machine_group_ids',
						'machine_ids',
						'property_types',
					].includes(key)) {
						possibleQueryParams[key] = query[key]
					}
				})
				if (Object.keys(possibleQueryParams).length > 0) {
					const filtered = res.filter((element) => {
						return elementMatchesQueryParams(element, possibleQueryParams)
					})
					resolve(filtered)
				} else {
					resolve(res)
				}
			})
			.catch((err) => {
				reject(err)
			})
	})
}

function getBasedMachines(companyId) {
	return dataTransferWrap(
		ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].getBasedMachines,
		{
			urlParams: {
				companyId: companyId,
			},
		})
}

function getExtendedMachines(companyId) {
	return dataTransferWrap(
		ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].getExtendedMachines,
		{
			urlParams: {
				companyId: companyId,
			},
		})
}

function getFulledMachines(companyId) {
	return dataTransferWrap(
		ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].getFulledMachines,
		{
			urlParams: {
				companyId: companyId,
			},
		})
}

function getPreviewMachine(companyId, machineId) {
	return dataTransferWrap(ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].getPreviewMachine, {
		urlParams: {
			companyId,
			machineId,
		},
	})
}

function getMachinePopup(companyId, machineId) {
	return dataTransferWrap(ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].getMachinePopup, {
		urlParams: {
			companyId,
			machineId,
		},
	})
}

function getMachine(companyId, machineId) {
	return dataTransferWrap(ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].getMachine, {
		urlParams: {
			companyId,
			machineId,
		},
	})
}

function deleteMachine(companyId, machineId) {
	return dataTransferWrap(ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].deleteMachine, {
		urlParams: {
			companyId,
			machineId
		},
	})
}

function editMachine(companyId, machineId, payload) {
	return dataTransferWrap(ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].editMachine, {
		urlParams: {
			companyId,
			machineId
		},
		...payload
	})
}

function putMachine(companyId, machineId, data) {
	return dataTransferWrap(
		ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].putMachine,
		{
			urlParams: {
				companyId,
				machineId,
			},
			...data,
		},
	)
}

function getFuelTank(companyId, machineId, fuelTankId) {
	return dataTransferWrap(ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].getFuelTank, {
		urlParams: {
			companyId,
			machineId,
			fuelTankId
		}
	})
}

function createFuelTank(companyId, machineId, payload) {
	return dataTransferWrap(ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].createFuelTank, {
		urlParams: {
			companyId,
			machineId
		},
		...payload
	})
}

function editFuelTank(companyId, machineId, fuelTankId, payload) {
	return dataTransferWrap(ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].editFuelTank, {
		urlParams: {
			companyId,
			machineId,
			fuelTankId
		},
		...payload
	})
}

function deleteFuelTank(companyId, machineId, fuelTankId) {
	return dataTransferWrap(ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].deleteFuelTank, {
		urlParams: {
			companyId,
			machineId,
			fuelTankId
		},
	})
}

function getSensorsValues(companyId, machineId, payload) {
	return dataTransferWrap(ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].getSensorsValues, {
		urlParams: {
			companyId,
			machineId,
		},
		params: payload
	})
}

function getRawGPSParams(companyId, machineId, payload) {
	return dataTransferWrap(ModulesMap.MACHINES, dtConfig[ModulesMap.MACHINES].getRawGPSParams, {
		urlParams: {
			companyId,
			machineId,
		},
		params: payload
	})
}

export default {
	createMachine,
	getAvailableMachines,
	getPreviewMachine,
	deleteMachine,
	getMachine,
	editMachine,
	getBasedMachines,
	getExtendedMachines,
	getMachinePopup,
	putMachine,
	getFulledMachines,
	getSensorsValues,
	getRawGPSParams,
	// Fuel tank
	getFuelTank,
	createFuelTank,
	editFuelTank,
	deleteFuelTank,
}
